import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';

const CAT_LABELS = { non_lu: '📬 Non lu', important: '🔴 Important', to_treat: '🟡 À traiter', ignored: '🗑️ Ignoré' };
const CAT_COLORS = { non_lu: '#6C63FF', important: '#EF5350', to_treat: '#FF9800', ignored: '#AAAACC' };

export default function Emails() {
  const location = useLocation();
  const [inbox, setInbox] = useState([]);
  const [rules, setRules] = useState([]);
  const [account, setAccount] = useState(null);
  const [tab, setTab] = useState('inbox');
  const [filter, setFilter] = useState(location.state?.filter || 'non_lu');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState('');
  const [openEmail, setOpenEmail] = useState(null);
  const [ruleForm, setRuleForm] = useState({ type: 'important', match_field: 'from', pattern: '', auto_task: false, login_url: '', phone_number: '' });
  const [savingRule, setSavingRule] = useState(false);

  const load = useCallback(() => {
    api.get('/email/inbox').then(r => setInbox(r.data)).catch(() => {});
    api.get('/email/rules').then(r => setRules(r.data)).catch(() => {});
    api.get('/email/account').then(r => setAccount(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true); setSyncResult('');
    try {
      const { data } = await api.post('/email/sync');
      setSyncResult(data.error ? `❌ ${data.error}` : `✅ ${data.synced} nouveau${data.synced !== 1 ? 'x' : ''} email${data.synced !== 1 ? 's' : ''} récupéré${data.synced !== 1 ? 's' : ''}`);
      load();
    } catch (e) { setSyncResult('❌ ' + (e.response?.data?.error || e.message)); }
    setSyncing(false);
    setTimeout(() => setSyncResult(''), 4000);
  };

  const markRead = async (email) => {
    if (!email.is_read) { await api.put(`/email/inbox/${email.id}/read`); load(); }
  };

  const changeCategory = async (id, category) => {
    await api.put(`/email/inbox/${id}/category`, { category });
    load();
  };

  const deleteEmail = async (id) => {
    await api.delete(`/email/inbox/${id}`);
    setOpenEmail(null); load();
  };

  const addRule = async () => {
    if (!ruleForm.pattern.trim()) return;
    setSavingRule(true);
    try {
      await api.post('/email/rules', ruleForm);
      setRuleForm({ type: 'important', match_field: 'from', pattern: '', auto_task: false, login_url: '', phone_number: '' });
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSavingRule(false);
  };

  const deleteRule = async (id) => { await api.delete(`/email/rules/${id}`); load(); };
  const toggleAutoTask = async (rule) => { await api.put(`/email/rules/${rule.id}`, { auto_task: !rule.auto_task }); load(); };

  const filtered = inbox.filter(e => e.category === filter);
  const unreadNonLu = inbox.filter(e => e.category === 'non_lu' && !e.is_read).length;
  const unreadImportant = inbox.filter(e => e.category === 'important' && !e.is_read).length;
  const unreadToTreat = inbox.filter(e => e.category === 'to_treat' && !e.is_read).length;

  return (
    <div>
      <div className="topbar">
        <div className="page-title">📧 Emails</div>
        {account && (
          <button className="btn btn-ghost btn-sm" onClick={sync} disabled={syncing}>
            {syncing ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '↺ Sync'}
          </button>
        )}
      </div>

      <div className="page" style={{ paddingTop: 0 }}>
        <div className="tabs">
          <button className={`tab${tab === 'inbox' ? ' active' : ''}`} onClick={() => setTab('inbox')}>
            📬 Boîte {(unreadNonLu + unreadImportant + unreadToTreat) > 0 && `(${unreadNonLu + unreadImportant + unreadToTreat})`}
          </button>
          <button className={`tab${tab === 'rules' ? ' active' : ''}`} onClick={() => setTab('rules')}>
            ⚙️ Règles {rules.length > 0 && `(${rules.length})`}
          </button>
        </div>

        {syncResult && (
          <div style={{ background: syncResult.startsWith('❌') ? '#FFEBEE' : '#E8F5E9', color: syncResult.startsWith('❌') ? '#C62828' : '#2E7D32', borderRadius: 12, padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
            {syncResult}
          </div>
        )}

        {/* Pas de compte configuré */}
        {!account && tab === 'inbox' && (
          <div className="empty-state">
            <div className="empty-icon">📧</div>
            <div className="empty-text">Aucune boîte email configurée<br />Allez dans Réglages → Boîte email</div>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => window.location.href = '/parametres'}>→ Réglages</button>
          </div>
        )}

        {/* BOÎTE DE RÉCEPTION */}
        {tab === 'inbox' && account && (
          <>
            {/* Filtres catégories */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              {['non_lu', 'important', 'to_treat'].map(cat => {
                const count = inbox.filter(e => e.category === cat && !e.is_read).length;
                return (
                  <button key={cat} onClick={() => setFilter(cat)}
                    style={{ padding: '7px 14px', borderRadius: 20, border: `2px solid ${filter === cat ? CAT_COLORS[cat] : '#E0E4F0'}`, background: filter === cat ? CAT_COLORS[cat] + '18' : 'white', fontSize: 13, fontWeight: filter === cat ? 700 : 500, color: filter === cat ? CAT_COLORS[cat] : '#888', cursor: 'pointer' }}>
                    {CAT_LABELS[cat]} {count > 0 && <span style={{ background: CAT_COLORS[cat], color: 'white', borderRadius: '50%', padding: '1px 6px', fontSize: 11, marginLeft: 4 }}>{count}</span>}
                  </button>
                );
              })}
              <button onClick={sync} disabled={syncing} style={{ marginLeft: 'auto', padding: '7px 14px', borderRadius: 20, border: '2px solid #E0E4F0', background: 'white', fontSize: 12, color: '#6C63FF', cursor: 'pointer', fontWeight: 600 }}>
                {syncing ? '...' : '↺ Actualiser'}
              </button>
            </div>

            {account?.last_sync && (
              <div style={{ fontSize: 11, color: '#AAAACC', marginBottom: 10 }}>
                Dernière sync : {new Date(account.last_sync).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}

            {filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">{filter === 'important' ? '🎉' : filter === 'non_lu' ? '📭' : '✅'}</div>
                <div className="empty-text">{filter === 'important' ? 'Aucun email important' : filter === 'non_lu' ? 'Aucun email non lu' : 'Aucun email à traiter'}<br />Cliquez sur "Actualiser" pour vérifier</div>
              </div>
            ) : (
              filtered.map(email => (
                <div key={email.id}
                  onClick={() => { setOpenEmail(email); markRead(email); }}
                  style={{ background: email.is_read ? '#F8F9FF' : 'white', borderRadius: 14, padding: '14px 16px', marginBottom: 8, cursor: 'pointer', boxShadow: email.is_read ? 'none' : '0 2px 12px rgba(0,0,0,0.08)', borderLeft: `4px solid ${CAT_COLORS[email.category]}`, opacity: email.is_read ? 0.8 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                        {!email.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: CAT_COLORS[email.category], flexShrink: 0 }} />}
                        <div style={{ fontWeight: email.is_read ? 500 : 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333355' }}>{email.subject}</div>
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{email.from_name || email.from_address}</div>
                      <div style={{ fontSize: 12, color: '#AAAACC', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.body_preview}</div>
                    </div>
                    <div style={{ fontSize: 11, color: '#BBBBCC', flexShrink: 0, textAlign: 'right' }}>
                      {new Date(email.received_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      {email.auto_task_created && <div style={{ color: '#4CAF50', fontWeight: 700 }}>✅ Tâche créée</div>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* RÈGLES */}
        {tab === 'rules' && (
          <>
            <div style={{ background: '#F5F4FF', borderRadius: 12, padding: 14, marginBottom: 16, fontSize: 13, color: '#555577', lineHeight: 1.6 }}>
              <strong>💡 Comment ça marche :</strong><br />
              • Les règles <strong style={{ color: '#EF5350' }}>Ignorer</strong> sont appliquées en premier → l'email ne remonte jamais<br />
              • Les règles <strong style={{ color: '#4CAF50' }}>Important</strong> font remonter l'email avec une option de créer une tâche auto<br />
              • Sans règle correspondante : Claude analyse (si configuré) ou l'email arrive en "Non lu"
            </div>

            {/* Ajouter une règle */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>➕ Nouvelle règle</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label className="form-label">Type</label>
                  <select className="form-input" value={ruleForm.type} onChange={e => setRuleForm({ ...ruleForm, type: e.target.value })}>
                    <option value="important">🔴 Toujours important</option>
                    <option value="ignore">🗑️ Toujours ignorer</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Chercher dans</label>
                  <select className="form-input" value={ruleForm.match_field} onChange={e => setRuleForm({ ...ruleForm, match_field: e.target.value })}>
                    <option value="from">Expéditeur</option>
                    <option value="domain">Domaine (@...)</option>
                    <option value="subject">Sujet</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Motif à détecter</label>
                <input className="form-input" placeholder="Ex: creditagricole.fr, devis, orchestra..." value={ruleForm.pattern}
                  onChange={e => setRuleForm({ ...ruleForm, pattern: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && addRule()} />
              </div>
              {ruleForm.type === 'important' && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input type="checkbox" id="auto_task" checked={ruleForm.auto_task} onChange={e => setRuleForm({ ...ruleForm, auto_task: e.target.checked })} />
                    <label htmlFor="auto_task" style={{ fontSize: 13, fontWeight: 600, color: '#555577', cursor: 'pointer' }}>
                      ✅ Créer une tâche automatiquement quand ce mail arrive
                    </label>
                  </div>
                  <div style={{ background: '#F0F4FF', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6C63FF', marginBottom: 10 }}>🔗 Accès rapide depuis l'email (optionnel)</div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label className="form-label">Site web / lien de connexion</label>
                      <input className="form-input" placeholder="https://www.portailfamille.fr/connexion" value={ruleForm.login_url}
                        onChange={e => setRuleForm({ ...ruleForm, login_url: e.target.value })} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Numéro de téléphone</label>
                      <input className="form-input" placeholder="05 46 00 00 00" value={ruleForm.phone_number}
                        onChange={e => setRuleForm({ ...ruleForm, phone_number: e.target.value })} />
                    </div>
                    <div style={{ fontSize: 11, color: '#AAAACC', marginTop: 6 }}>Ces boutons apparaîtront directement dans l'email — un clic ouvre le site ou appelle</div>
                  </div>
                </>
              )}
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={addRule} disabled={savingRule || !ruleForm.pattern.trim()}>
                {savingRule ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '+ Ajouter la règle'}
              </button>
            </div>

            {/* Liste des règles */}
            {rules.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <div className="empty-text">Aucune règle définie<br />Ajoutez vos premières règles ci-dessus</div>
              </div>
            ) : (
              <>
                {['important', 'ignore'].map(type => {
                  const typeRules = rules.filter(r => r.type === type);
                  if (!typeRules.length) return null;
                  return (
                    <div key={type}>
                      <div className="section-title" style={{ color: type === 'important' ? '#EF5350' : '#888' }}>
                        {type === 'important' ? '🔴 Toujours important' : '🗑️ Toujours ignorer'}
                      </div>
                      {typeRules.map(rule => (
                        <div key={rule.id} style={{ background: 'white', borderRadius: 12, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#333355' }}>{rule.pattern}</div>
                            <div style={{ fontSize: 11, color: '#AAAACC' }}>
                              {rule.match_field === 'from' ? 'dans expéditeur' : rule.match_field === 'domain' ? 'domaine' : 'dans sujet'}
                              {rule.auto_task ? ' · ✅ Tâche auto' : ''}
                              {rule.login_url ? ' · 🔗 Site' : ''}
                              {rule.phone_number ? ` · 📞 ${rule.phone_number}` : ''}
                            </div>
                          </div>
                          {type === 'important' && (
                            <button onClick={() => toggleAutoTask(rule)} title="Tâche automatique"
                              style={{ background: rule.auto_task ? '#E8F5E9' : '#F5F5F5', color: rule.auto_task ? '#2E7D32' : '#AAA', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                              ✅ Tâche
                            </button>
                          )}
                          <button onClick={() => deleteRule(rule.id)}
                            style={{ background: '#FFEBEE', color: '#EF5350', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* MODAL LECTURE EMAIL */}
      {openEmail && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setOpenEmail(null)}>
          <div className="modal" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-handle" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: CAT_COLORS[openEmail.category], flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: CAT_COLORS[openEmail.category], fontWeight: 700 }}>{CAT_LABELS[openEmail.category]}</span>
            </div>
            <div className="modal-title" style={{ fontSize: 15, lineHeight: 1.4 }}>{openEmail.subject}</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>De : {openEmail.from_name} &lt;{openEmail.from_address}&gt;</div>
            <div style={{ fontSize: 12, color: '#BBBBCC', marginBottom: 14 }}>
              {new Date(openEmail.received_at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
            </div>
            <div style={{ background: '#F8F9FF', borderRadius: 10, padding: 14, fontSize: 13, color: '#444466', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 16 }}>
              {openEmail.body_preview || '(aperçu non disponible)'}
            </div>
            {(openEmail.login_url || openEmail.phone_number) && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {openEmail.login_url && (
                  <a href={openEmail.login_url} target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#6C63FF', color: 'white', borderRadius: 12, padding: '13px 16px', fontSize: 14, fontWeight: 700, textDecoration: 'none', minWidth: 140 }}>
                    🔗 Ouvrir le site
                  </a>
                )}
                {openEmail.phone_number && (
                  <a href={`tel:${openEmail.phone_number.replace(/\s/g,'')}`}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#4CAF50', color: 'white', borderRadius: 12, padding: '13px 16px', fontSize: 14, fontWeight: 700, textDecoration: 'none', minWidth: 140 }}>
                    📞 {openEmail.phone_number}
                  </a>
                )}
              </div>
            )}
            {openEmail.auto_task_created && (
              <div style={{ background: '#E8F5E9', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#2E7D32', marginBottom: 12 }}>
                ✅ Tâche créée automatiquement depuis cet email
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {openEmail.category !== 'important' && (
                <button className="btn btn-ghost btn-sm" style={{ color: '#EF5350' }} onClick={() => { changeCategory(openEmail.id, 'important'); setOpenEmail(null); }}>🔴 Important</button>
              )}
              {openEmail.category !== 'to_treat' && (
                <button className="btn btn-ghost btn-sm" style={{ color: '#FF9800' }} onClick={() => { changeCategory(openEmail.id, 'to_treat'); setOpenEmail(null); }}>🟡 À traiter</button>
              )}
              <button className="btn btn-ghost btn-sm" onClick={() => { changeCategory(openEmail.id, 'ignored'); setOpenEmail(null); }}>🗑️ Ignorer</button>
              <button className="btn btn-danger btn-sm" onClick={() => deleteEmail(openEmail.id)}>Supprimer</button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setOpenEmail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
